import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  ADD_COMPANY_MEMBER,
  Company,
  ErrorResponse,
  GET_ALL_COMPANY,
  GET_MY_COMPANIES,
} from '@frontend/data-access';
import { App, Button, Form, Modal, Select } from 'antd';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { ReactNode, useState } from 'react';

import CompanyAvatar from '../company-avatar/company-avatar';

const { Option } = Select;

/* eslint-disable-next-line */
export interface CompanyAddProps {
  onAfterAdd?: () => void;
  children?: ReactNode;
}

export function CompanyAdd({
  children,
  onAfterAdd = () => undefined,
}: CompanyAddProps) {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { t } = useTranslation('common');

  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: error.message,
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };
  const handleCompleted = () => {
    notification.success({
      message: t('success'),
      description: t('add_institution_success'),
    });
    setIsModalOpen(false);
    onAfterAdd();
  };

  const { data: companies } = useQuery<{
    allCompanies: Company[];
  }>(GET_ALL_COMPANY);
  // const { data: myCompanies } = useQuery<{
  //   myCompanies: Company[];
  // }>(GET_MY_COMPANIES);
  const [addCompanyMember, { loading: addCompanyMemberLoading }] = useMutation(
    ADD_COMPANY_MEMBER,
    {
      onError: handleError,
      onCompleted: handleCompleted,
      refetchQueries: [{ query: GET_MY_COMPANIES }],
    }
  );

  const handleFinish = async (values: { companyIds: number[] }) => {
    await addCompanyMember({
      variables: {
        companyIds: values.companyIds,
      },
    });
    form.resetFields();
  };

  // useEffect(() => {
  //   form.setFieldValue(
  //     'companyIds',
  //     myCompanies?.myCompanies?.map((company) => company.id) ?? ''
  //   );
  // }, [form, myCompanies]);

  return (
    <div className="">
      <Button
        size="middle"
        type="primary"
        shape="round"
        icon={<PlusOutlined />}
        onClick={() => setIsModalOpen(true)}
        data-test="add-institution-btn"
      >
        {children}
      </Button>
      <Modal
        title={t('add_institution')}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        okText={t('save')}
        cancelButtonProps={{
          shape: 'round',
        }}
        okButtonProps={{
          shape: 'round',
          loading: addCompanyMemberLoading,
          onClick: () => {
            form.submit();
          },
        }}
      >
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            name="companyIds"
            label={t('work_for_institution')}
            tooltip={t('work_for_institution')}
            rules={[{ required: true, message: t('select_institution') }]}
          >
            <Select
              mode="multiple"
              allowClear
              placeholder={t('search_institution')}
              showSearch
              filterOption={(input, option) =>
                ((option?.label as string) ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              notFoundContent={
                <div className="p-2 text-center">
                  {t('institution_not_found')}
                </div>
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div className="p-2 text-center">
                    <Link href="/company/onboarding/basics">
                      <Button
                        shape="round"
                        data-test="add-institution-create-btn"
                      >
                        {t('create_new_institution')}
                      </Button>
                    </Link>
                  </div>
                </>
              )}
              data-test="add-institution-select"
            >
              {companies?.allCompanies?.map((company) => (
                <Option
                  key={company.id}
                  value={company.id}
                  label={company.name}
                >
                  <div className="flex gap-4 items-center">
                    <CompanyAvatar company={company} size={20} />
                    {company.name}
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default CompanyAdd;
